// Sharing press-kit files as a single Dropbox link.
//
// A share is a folder of COPIES with one public link on it, because a Dropbox
// link points at exactly one object — there is no link over a set of files.
// The copies cost quota, so every share expires and a nightly job deletes the
// folder; see the comms.asset_shares migration for the whole argument.
//
// Two things drive the shape of this UI:
//
//   * People will open a share folder in Finder and start working in it unless
//     told not to. So the warning is not a footnote — it is stated before the
//     share is made, again next to the finished link, and left in the folder
//     itself as a text file for whoever never sees this screen.
//
//   * Comms sends low-res first and the high-res of the same photographs on
//     request. Whether a photograph HAS a high-res sibling depends on what the
//     photographer delivered and varies file by file, so the modal previews
//     that per file before anything is copied, and a finished share can be
//     re-sent at another resolution without re-picking anything.
//
//   * A link that has been sent cannot be replaced by a second link when three
//     more photographs are wanted. So the modal can put the selection into a
//     share that already exists — the files land in the folder the journalist
//     already has open, and the preview says which of them are in there
//     already rather than copying anything twice.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../supabaseClient';
import { VERSION_OPTIONS, VERSION_PREF_AUTO, VERSION_PREF_ALL } from './assetVariants';
import { callEdgeFunction, formatFileSize } from './pressAssets';
import { S, GOLD, CommsModal, Field, CInput, FilterBtn, BtnGhost, BtnPrimary } from './commsUi';

const comms = supabase.schema('comms');

export const SHARE_DURATIONS = [
  { days: 7,   label: '7 days' },
  { days: 30,  label: '30 days' },
  { days: 90,  label: '90 days' },
];
export const DEFAULT_SHARE_DAYS = 30;
export const MAX_SHARE_DAYS = 365;

// "As selected" plus the concrete resolutions. "Best available" and "All
// versions" are browsing preferences and mean nothing for a share: a share has
// to name one file per photograph.
export const RESOLUTION_OPTIONS = [
  { value: 'source', label: 'As selected' },
  ...VERSION_OPTIONS.filter(o => o.value !== VERSION_PREF_AUTO && o.value !== VERSION_PREF_ALL),
];

export const resolutionLabel = value =>
  // 'mixed' is what a share becomes once files are added to it at a different
  // resolution; the per-file truth is in comms.asset_share_files.
  (value === 'mixed' ? 'Mixed resolutions' : null)
  || RESOLUTION_OPTIONS.find(o => o.value === value)?.label || value || 'As selected';

export function previewShare(files, resolution, shareId) {
  return callEdgeFunction('share-press-selection', {
    mode: 'preview', files, resolution, ...(shareId ? { shareId } : {}),
  });
}

/**
 * Puts more files into a share that already exists.
 *
 * Files the share already holds are skipped by the server rather than copied a
 * second time, so re-sending a selection that overlaps costs nothing.
 */
export function addToShare({
  shareId, files, resolution, onlyExact, extendDays, description, contactVendorId, contactPersonId,
}) {
  return callEdgeFunction('share-press-selection', {
    mode: 'add', shareId, files, resolution, onlyExact, extendDays, description,
    // Only used when the share was made without a contact: naming one later is
    // filling a blank, never a correction.
    contactVendorId, contactPersonId,
  });
}

export function createShare({
  files, resolution, label, description, expiresDays, onlyExact, projectCode,
  contactVendorId, contactPersonId,
}) {
  return callEdgeFunction('share-press-selection', {
    mode: 'create', files, resolution, label, description, expiresDays, onlyExact, projectCode,
    contactVendorId, contactPersonId,
  });
}

// Offered as one-click answers because they are what almost every share is:
// the field stays free text for the ones that are not.
export const SHARE_PURPOSES = ['Pitch', 'Press release', 'For project review', 'Requested images'];

/**
 * The press register, as the share modal needs it: publications with their
 * people underneath.
 *
 * Same source as the Contacts page — public.vendors, category Media, comms
 * enabled — so what you can share with is exactly who is in the register.
 */
export function usePressContacts() {
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    let stale = false;
    (async () => {
      try {
        const [{ data: vendors }, { data: people }] = await Promise.all([
          supabase.from('vendors').select('id, company_name')
            .eq('category', 'Media').eq('comms_enabled', true).order('company_name'),
          supabase.from('vendor_contacts').select('id, vendor_id, name, designation')
            .order('name'),
        ]);
        if (stale) return;
        const byVendor = new Map();
        for (const p of people || []) {
          if (!byVendor.has(p.vendor_id)) byVendor.set(p.vendor_id, []);
          byVendor.get(p.vendor_id).push(p);
        }
        setContacts((vendors || []).map(v => ({ ...v, people: byVendor.get(v.id) || [] })));
      } catch {
        setContacts([]);
      }
    })();
    return () => { stale = true; };
  }, []);

  return contacts;
}

export function revokeShare(shareId) {
  return callEdgeFunction('purge-expired-shares', { shareId });
}

export function daysUntil(iso) {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

export function expiryLabel(iso) {
  const days = daysUntil(iso);
  if (days < 0) return 'expired — deletes tonight';
  if (days === 0) return 'deletes today';
  if (days === 1) return 'deletes tomorrow';
  return `deletes in ${days} days`;
}

/**
 * The shares for a project, newest first.
 *
 * `active` only by default, which is what somewhere offering a live link needs.
 * `includePast` adds the shares whose folders have been deleted: the folder is
 * gone but the row and its file list stay, and "what did we send Dezeen in
 * August" is a question asked long after the link died.
 */
export function useAssetShares(projectCode, rev = 0, { includePast = false } = {}) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!projectCode) { setShares([]); return; }
    setLoading(true);
    try {
      let query = comms.from('asset_shares').select('*').eq('project_code', projectCode);
      if (!includePast) query = query.eq('status', 'active');
      const { data, error } = await query.order('created_at', { ascending: false });
      if (error) throw error;
      setShares(data || []);
    } catch {
      setShares([]);
    } finally {
      setLoading(false);
    }
  }, [projectCode, includePast]);

  useEffect(() => { refresh(); }, [refresh, rev]);

  return { shares, loading, refresh };
}

/**
 * What a share holds, or held — the permanent record.
 *
 * Rows outlive the Dropbox folder: purging a share deletes the folder and
 * leaves these alone. `origin` says whether the app copied a file in or found
 * it there; `removed_at` marks a file that was taken out of the folder by hand,
 * which does not unsend it.
 */
export async function shareContents(shareId) {
  const { data, error } = await comms.from('asset_share_files')
    .select('id, file_name, size_bytes, resolution, is_fallback, origin, removed_at, source_path')
    .eq('share_id', shareId)
    .order('file_name');
  if (error) throw error;
  return data || [];
}

/**
 * Compares a live share's folder with the record and writes down the
 * difference. Dropbox does not announce a file somebody drags into the folder,
 * so the only way to know is to look.
 */
export function syncShare(shareId) {
  return callEdgeFunction('share-press-selection', { mode: 'sync', shareId });
}

export async function shareFiles(shareId) {
  const { data, error } = await comms.from('asset_share_files')
    .select('source_file_id, source_path, file_name, resolution')
    .eq('share_id', shareId);
  if (error) throw error;
  return (data || []).map(r => ({ path: r.source_path, fileId: r.source_file_id, name: r.file_name }));
}

// ── The standing warning ────────────────────────────────────────────────────
// Shown before the share is made and again beside the finished link. The same
// text goes into the folder as "ABOUT THIS FOLDER.txt" for anyone who opens it
// in Dropbox without ever seeing this screen.
function CopyWarning({ expiresAt }) {
  return (
    <div style={{ background: '#F3EFDF', border: '1px solid rgba(159,144,42,.35)', borderRadius: 3, padding: '10px 12px', marginBottom: 14 }}>
      <div style={{ ...S.eyebrow, color: '#7A6020', marginBottom: 4 }}>Copies, not the originals</div>
      <div style={{ ...S.meta, fontSize: 11.5, lineHeight: 1.55, color: '#5C5A54' }}>
        A share folder holds copies. Nothing done inside it comes back into the press
        kit, so never edit, rename or file work there — use the press kit itself.
        {' '}{expiresAt
          ? <>The folder and its link are <strong>deleted automatically on {new Date(expiresAt).toLocaleDateString()}</strong>.</>
          : <>The folder and its link are <strong>deleted automatically when the share expires</strong>.</>}
      </div>
    </div>
  );
}

function StateNote({ preview }) {
  // A reply that is not the shape we expect is treated as no preview rather
  // than being destructured blindly: this renders inside the modal, so an
  // exception here takes the whole share dialog down and the user loses the
  // selection they came in with.
  const totals = preview?.totals;
  const files = preview?.files;
  if (!totals || !Array.isArray(files)) return null;
  const fallbacks = files.filter(f => f.state === 'fallback' && !f.already);
  const already = totals.already || 0;

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ ...S.meta, fontSize: 12, marginBottom: fallbacks.length ? 8 : 0 }}>
        {totals.sending} file{totals.sending === 1 ? '' : 's'} · {formatFileSize(totals.bytes)}
        {totals.exact > 0 && totals.fallback + totals.onlyVersion > 0 ? ` · ${totals.exact} at the resolution you asked for` : ''}
        {already > 0 ? ` · ${already} already in this share, not copied again` : ''}
      </div>
      {fallbacks.length > 0 && (
        <div style={{ background: '#F3EFDF', border: '1px solid rgba(159,144,42,.35)', borderRadius: 3, padding: '10px 12px' }}>
          <div style={{ ...S.eyebrow, color: '#7A6020', marginBottom: 5 }}>
            {fallbacks.length} file{fallbacks.length === 1 ? ' has' : 's have'} no version at this resolution
          </div>
          <div style={{ ...S.meta, fontSize: 11.5, lineHeight: 1.5, color: '#5C5A54', maxHeight: 96, overflowY: 'auto' }}>
            {fallbacks.map(f => (
              <div key={f.selectedPath}>{f.name}{f.note ? ` — ${f.note}` : ''}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Turns the current selection into one Dropbox link — a new one, or one that
 * has already been sent.
 *
 * The preview runs on the server, using the same resolution pass the copy will
 * use — so what this shows is exactly what gets sent, rather than a guess the
 * copy might contradict. When a share is picked as the destination the preview
 * also names the files that share already holds, which get skipped rather than
 * copied twice.
 */
export function ShareAssetsModal({ assets, projectCode, onClose, onDone, showToast }) {
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [days, setDays] = useState(DEFAULT_SHARE_DAYS);
  const [customDays, setCustomDays] = useState('');
  const [resolution, setResolution] = useState('source');
  const [onlyExact, setOnlyExact] = useState(false);
  // '' means a new share; otherwise the id of the live share to add to.
  const [targetId, setTargetId] = useState('');
  const [extend, setExtend] = useState(false);
  // '' = nobody in particular. Otherwise 'v:<vendorId>' for a publication or
  // 'p:<vendorId>:<personId>' for one of its people.
  const [contact, setContact] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const files = useMemo(
    () => assets.map(a => ({ path: a.path, fileId: a.fileId || null })),
    [assets],
  );

  const { shares: liveShares } = useAssetShares(projectCode);
  const contacts = usePressContacts();
  const target = liveShares.find(s => s.id === targetId) || null;
  // A share about to be deleted is a poor place to put new files, so extending
  // it is offered — but only when it is actually close, so the common case
  // stays a single decision.
  const expiringSoon = target ? daysUntil(target.expires_at) <= 7 : false;

  // Re-previews whenever the resolution changes. Responses that arrive after a
  // newer request are dropped, or a slow answer for the old resolution would
  // overwrite the one on screen.
  useEffect(() => {
    let stale = false;
    setPreviewing(true);
    setPreviewError(null);
    previewShare(files, resolution, targetId || undefined)
      .then(res => { if (!stale) setPreview(res); })
      .catch(e => { if (!stale) { setPreview(null); setPreviewError(e.message); } })
      .finally(() => { if (!stale) setPreviewing(false); });
    return () => { stale = true; };
  }, [files, resolution, targetId]);

  const effectiveDays = customDays ? Math.min(Math.max(Number(customDays) || 0, 1), MAX_SHARE_DAYS) : days;
  const expiresAt = useMemo(
    () => new Date(Date.now() + effectiveDays * 86400000).toISOString(),
    [effectiveDays],
  );
  const sending = preview?.totals
    ? (onlyExact
        ? (preview.files || []).filter(f => !f.already && f.state !== 'fallback').length
        : preview.totals?.sending ?? assets.length)
    : assets.length;

  const [, contactVendorId, contactPersonId] = (() => {
    if (!contact) return [null, null, null];
    const [kind, vendorId, personId] = contact.split(':');
    return [kind, Number(vendorId), personId ? Number(personId) : null];
  })();

  const submit = async () => {
    if (busy || !sending) return;
    setBusy(true);
    try {
      const res = target
        ? await addToShare({
            shareId: target.id,
            files,
            resolution,
            onlyExact,
            extendDays: extend ? DEFAULT_SHARE_DAYS : 0,
            description: description.trim(),
            contactVendorId,
            contactPersonId,
          })
        : await createShare({
            files, resolution, label: label.trim(), description: description.trim(),
            expiresDays: effectiveDays, onlyExact, projectCode,
            contactVendorId, contactPersonId,
          });
      setResult(res);
      onDone?.(res);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.share.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast('Could not copy — select the link and copy it by hand', 'error');
    }
  };

  if (result) {
    const added = Number(result.added || 0);
    return (
      <CommsModal title={added ? 'Files added to the share' : 'Share link ready'} onClose={onClose} width={560}>
        {added > 0 && (
          <div style={{ ...S.meta, fontSize: 11.5, marginBottom: 12, color: '#5C5A54' }}>
            {added} file{added === 1 ? '' : 's'} added to “{result.share.label}”. The link below is
            the one that was already sent — it now shows the new files too.
            {result.skipped ? ` ${result.skipped} of the files picked ${result.skipped === 1 ? 'was' : 'were'} already in the folder and ${result.skipped === 1 ? 'was' : 'were'} not copied again.` : ''}
            {result.extended ? ' Its expiry has been pushed back.' : ''}
          </div>
        )}
        {result.reused && (
          <div style={{ ...S.meta, fontSize: 11.5, marginBottom: 12, color: '#5C5A54' }}>
            These exact files were already shared at this resolution, so that share is
            reused rather than copying everything a second time.
            {result.extended ? ' Its expiry has been extended to match what you asked for.' : ''}
          </div>
        )}
        <Field label="Link">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input readOnly value={result.share.share_url || ''} onFocus={e => e.target.select()} style={{ ...S.inputStyle, fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }} />
            <BtnGhost onClick={copyLink}>{copied ? 'Copied' : 'Copy'}</BtnGhost>
          </div>
        </Field>
        <div style={{ ...S.meta, fontSize: 11.5, marginBottom: 14 }}>
          {result.share.file_count} file{result.share.file_count === 1 ? '' : 's'} ·
          {' '}{formatFileSize(result.share.total_bytes)} ·
          {' '}{resolutionLabel(result.share.requested_resolution)} ·
          {' '}{expiryLabel(result.share.expires_at)}
          {result.share.description ? ` · ${result.share.description}` : ''}
          {result.share.contact_label ? ` · for ${result.share.contact_label}` : ''}
        </div>
        <CopyWarning expiresAt={result.share.expires_at} />
        {result.dropboxExpirySet === false && (
          <div style={{ ...S.meta, fontSize: 11.5, color: '#7A6020', marginBottom: 12 }}>
            Dropbox would not put an expiry on the link itself, so the link stays live
            until the folder behind it is deleted on the date above.
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <BtnPrimary onClick={onClose}>Done</BtnPrimary>
        </div>
      </CommsModal>
    );
  }

  return (
    <CommsModal title={target ? 'Add to a share already sent' : 'Share as one Dropbox link'} onClose={onClose} width={560}>
      <div style={{ ...S.body, marginBottom: 14 }}>
        {target
          ? <>Copies the {assets.length} selected file{assets.length === 1 ? '' : 's'} into
              “{target.label}”, the folder its link already points at. Anything already
              in there is left alone.</>
          : <>Copies the {assets.length} selected file{assets.length === 1 ? '' : 's'} into a
              dated folder and gives you one link to it.</>}
      </div>

      {liveShares.length > 0 && (
        <Field label="Where do these go?">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <FilterBtn label="A new link" active={!targetId} onClick={() => { setTargetId(''); setExtend(false); }} />
            {liveShares.map(sh => (
              <FilterBtn key={sh.id} label={sh.label} active={targetId === sh.id}
                onClick={() => setTargetId(sh.id)} />
            ))}
          </div>
          {target && (
            <div style={{ ...S.meta, fontSize: 11.5, marginTop: 6 }}>
              {target.file_count} file{target.file_count === 1 ? '' : 's'} in it already ·
              {' '}{resolutionLabel(target.requested_resolution)} · {expiryLabel(target.expires_at)}
            </div>
          )}
        </Field>
      )}

      {!target && (
        <CInput label="What is this share for?" value={label} onChange={setLabel}
          placeholder="e.g. Dezeen — Ambika River Home" />
      )}

      {/* Why it went. The label names the share and the Dropbox folder; this
          says what it was for, which is the part nobody can reconstruct from a
          folder name six months later. Suggestions because almost every share
          is one of four things, free text because some are not. */}
      {(!target || !target.description) && (
        <Field label="What is it for?">
          <input value={description} onChange={e => setDescription(e.target.value)}
            placeholder="e.g. pitch, press release, for project review"
            style={S.inputStyle} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {SHARE_PURPOSES.map(p => (
              <FilterBtn key={p} label={p} active={description === p}
                onClick={() => setDescription(description === p ? '' : p)} />
            ))}
          </div>
        </Field>
      )}

      {target?.description && (
        <div style={{ ...S.meta, fontSize: 11.5, marginBottom: 14 }}>
          Recorded as <strong>{target.description}</strong>.
        </div>
      )}

      {/* Who it is for, from the press register. Optional on purpose: a folder
          sent to whoever asks is a normal thing to make, and naming a contact
          that isn't real would be worse than naming none. A share that already
          has one keeps it — changing who it was for is a correction, not
          something to do while adding files. */}
      {(!target || !target.contact_label) && (
        <Field label="Share with">
          <select value={contact} onChange={e => setContact(e.target.value)} style={S.inputStyle}>
            <option value="">Nobody in particular</option>
            {contacts.map(v => (
              <optgroup key={v.id} label={v.company_name}>
                <option value={`v:${v.id}`}>{v.company_name}</option>
                {v.people.map(p => (
                  <option key={p.id} value={`p:${v.id}:${p.id}`}>
                    {`\u00A0\u00A0${p.name}${p.designation ? `, ${p.designation}` : ''}`}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <div style={{ ...S.meta, fontSize: 11.5, marginTop: 6 }}>
            Optional. Naming a publication or a person files this share on their page in
            Contacts, so what was sent to them stays findable after the folder is deleted.
          </div>
        </Field>
      )}

      {target?.contact_label && (
        <div style={{ ...S.meta, fontSize: 11.5, marginBottom: 14 }}>
          This share is recorded as sent to <strong>{target.contact_label}</strong>.
        </div>
      )}

      {target ? (
        expiringSoon && (
          <Field label="Deletes after">
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="checkbox" checked={extend} onChange={e => setExtend(e.target.checked)} style={{ marginTop: 3 }} />
              <span style={{ ...S.meta, fontSize: 11.5, lineHeight: 1.5 }}>
                This share {expiryLabel(target.expires_at)}. Give it another {DEFAULT_SHARE_DAYS} days
                from today, so the files being added do not go with it.
              </span>
            </label>
          </Field>
        )
      ) : (
      <Field label="Deletes after">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {SHARE_DURATIONS.map(d => (
            <FilterBtn key={d.days} label={d.label} active={!customDays && days === d.days}
              onClick={() => { setCustomDays(''); setDays(d.days); }} />
          ))}
          <input type="number" min={1} max={MAX_SHARE_DAYS} value={customDays}
            onChange={e => setCustomDays(e.target.value)} placeholder="days"
            style={{ ...S.inputStyle, width: 90 }} />
        </div>
        <div style={{ ...S.meta, fontSize: 11.5, marginTop: 6 }}>
          Deletes on {new Date(expiresAt).toLocaleDateString()} — the folder and the link both go.
        </div>
      </Field>
      )}

      <Field label="Resolution">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {RESOLUTION_OPTIONS.map(o => (
            <FilterBtn key={o.value} label={o.label} active={resolution === o.value}
              onClick={() => setResolution(o.value)} />
          ))}
        </div>
      </Field>

      {target && target.requested_resolution !== 'mixed' && target.requested_resolution !== resolution && (
        <div style={{ ...S.meta, fontSize: 11.5, color: '#7A6020', marginBottom: 14 }}>
          That share went out as {resolutionLabel(target.requested_resolution).toLowerCase()}. Adding
          these at {resolutionLabel(resolution).toLowerCase()} leaves one folder holding two
          resolutions — fine to do, worth saying in the email.
        </div>
      )}

      {previewing && <div style={{ ...S.meta, fontSize: 12, marginBottom: 14 }}>Checking what is available…</div>}
      {previewError && (
        <div style={{ ...S.meta, fontSize: 11.5, color: '#8A3A2A', marginBottom: 14 }}>{previewError}</div>
      )}
      {!previewing && !previewError && <StateNote preview={preview} />}

      {preview?.totals?.fallback > 0 && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={onlyExact} onChange={e => setOnlyExact(e.target.checked)} style={{ marginTop: 3 }} />
          <span style={{ ...S.meta, fontSize: 11.5, lineHeight: 1.5 }}>
            Leave those out and share only the {preview.totals.selected - preview.totals.fallback} file
            {preview.totals.selected - preview.totals.fallback === 1 ? '' : 's'} available at this resolution.
            Otherwise they go as selected.
          </span>
        </label>
      )}

      <CopyWarning expiresAt={target && !extend ? target.expires_at : expiresAt} />

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <BtnGhost onClick={onClose}>Cancel</BtnGhost>
        <BtnPrimary onClick={submit}>
          {busy
            ? 'Copying…'
            : target
              ? `Add ${sending} file${sending === 1 ? '' : 's'} to this share`
              : `Create link${sending ? ` for ${sending} file${sending === 1 ? '' : 's'}` : ''}`}
        </BtnPrimary>
      </div>
    </CommsModal>
  );
}

/**
 * The files in one share, live or long gone.
 *
 * For a live share this asks Dropbox first: a file somebody dragged into the
 * folder is invisible to the app until something looks, and the moment you are
 * asking what is in there is a good moment to find out. For a purged share
 * there is nothing to look at — the folder was deleted — so this is purely the
 * record, which is the point of keeping it.
 */
function ShareContents({ share, showToast }) {
  const [rows, setRows] = useState(null);
  const [checking, setChecking] = useState(false);
  const [note, setNote] = useState(null);

  const live = share.status === 'active';

  useEffect(() => {
    let stale = false;
    (async () => {
      if (live) {
        setChecking(true);
        try {
          const res = await syncShare(share.id);
          if (!stale && res?.adopted) {
            setNote(`${res.adopted} file${res.adopted === 1 ? '' : 's'} had been added in Dropbox by hand`);
          }
        } catch (e) {
          if (!stale) setNote(`Could not check Dropbox — showing the record as it stands (${e.message})`);
        } finally {
          if (!stale) setChecking(false);
        }
      }
      try {
        const data = await shareContents(share.id);
        if (!stale) setRows(data);
      } catch (e) {
        if (!stale) { setRows([]); showToast(e.message, 'error'); }
      }
    })();
    return () => { stale = true; };
  }, [share.id, live, showToast]);

  if (rows === null) {
    return <div style={{ ...S.meta, fontSize: 11.5, padding: '6px 0' }}>
      {checking ? 'Checking the folder…' : 'Loading…'}
    </div>;
  }
  if (!rows.length) {
    return <div style={{ ...S.meta, fontSize: 11.5, padding: '6px 0' }}>Nothing recorded for this share.</div>;
  }

  return (
    <div style={{ padding: '4px 0 2px' }}>
      {note && <div style={{ ...S.meta, fontSize: 11, color: '#7A6020', marginBottom: 6 }}>{note}</div>}
      {rows.map(f => (
        <div key={f.id} style={{
          ...S.meta, fontSize: 11.5, lineHeight: 1.6,
          display: 'flex', gap: 8, justifyContent: 'space-between',
          color: f.removed_at ? '#8E8C86' : '#4A4842',
        }}>
          <span style={{ textDecoration: f.removed_at ? 'line-through' : 'none' }}>{f.file_name}</span>
          <span style={{ whiteSpace: 'nowrap' }}>
            {f.origin === 'manual' && <em style={{ color: '#7A6020' }}>added in Dropbox · </em>}
            {f.is_fallback && <em>sent as selected · </em>}
            {f.removed_at ? 'taken out of the folder · ' : ''}
            {formatFileSize(f.size_bytes)}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * What has been sent to one contact — the same record, asked the other way
 * round.
 *
 * Lives on the contact's page in Contacts, where the question is "what have we
 * sent AD India" rather than "what is out from this project". Expired shares
 * stay listed: the folder is gone, what was in it is not, and that is exactly
 * what somebody picking up a conversation months later needs.
 */
export function ContactSharesPanel({ contactVendorId, showToast }) {
  const [shares, setShares] = useState(null);
  const [projectNames, setProjectNames] = useState({});
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let stale = false;
    if (!contactVendorId) { setShares([]); return undefined; }
    (async () => {
      try {
        const { data, error } = await comms.from('asset_shares')
          .select('*')
          .eq('contact_vendor_id', contactVendorId)
          .order('created_at', { ascending: false });
        if (error) throw error;
        if (stale) return;
        setShares(data || []);

        // The code alone says nothing to anyone who does not have the register
        // by heart. Read live rather than stored with the share, so a project
        // renamed since it was sent reads by the name it has now.
        //
        // Both registries, because they do not hold the same projects:
        // comms.projects covers the ones comms works on, public.projects is the
        // registry of record and has roughly three times as many. A share can be
        // made from any press kit, so a lookup in comms alone leaves some
        // projects showing a bare code forever. public wins where both answer,
        // matching how the comms Projects page itself resolves a name.
        const codes = [...new Set((data || []).map(sh => sh.project_code).filter(Boolean))];
        if (!codes.length) return;
        const [commsRows, registryRows] = await Promise.all([
          comms.from('projects').select('project_code, name').in('project_code', codes),
          supabase.from('projects').select('project_code, name')
            .in('project_code', codes).is('deleted_at', null),
        ]);
        if (stale) return;
        const names = {};
        for (const pr of commsRows.data || []) if (pr.project_code && pr.name) names[pr.project_code] = pr.name;
        for (const pr of registryRows.data || []) if (pr.project_code && pr.name) names[pr.project_code] = pr.name;
        setProjectNames(names);
      } catch {
        if (!stale) setShares([]);
      }
    })();
    return () => { stale = true; };
  }, [contactVendorId]);

  if (shares === null) return <div style={{ ...S.meta, fontSize: 12 }}>Loading…</div>;
  if (!shares.length) {
    return (
      <div style={{ ...S.meta, fontSize: 12 }}>
        No press files have been shared with this contact yet. Share them from a project&apos;s
        press kit and name this contact in the share.
      </div>
    );
  }

  return (
    <div>
      {shares.map(share => {
        const live = share.status === 'active';
        return (
          <div key={share.id} style={{ borderBottom: '1px solid rgba(27,27,25,.08)', padding: '8px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, color: '#1B1B19' }}>
                {share.label}
                {share.project_code ? (
                  <span style={{ ...S.meta, marginLeft: 8 }}>
                    {share.project_code}
                    {projectNames[share.project_code] ? ` — ${projectNames[share.project_code]}` : ''}
                  </span>
                ) : null}
              </span>
              <span style={{ ...S.meta, fontSize: 11 }}>
                sent {new Date(share.created_at).toLocaleDateString()}
                {live ? ` · ${expiryLabel(share.expires_at)}` : ' · folder deleted'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', marginTop: 2 }}>
              <span style={{ ...S.meta, fontSize: 11.5 }}>
                {share.description ? <span style={{ color: '#4A4842' }}>{share.description} · </span> : null}
                {share.file_count} file{share.file_count === 1 ? '' : 's'} ·
                {' '}{formatFileSize(share.total_bytes)} ·
                {' '}{resolutionLabel(share.requested_resolution)}
                {share.contact_label ? ` · ${share.contact_label}` : ''}
              </span>
              {live && share.share_url && (
                <a href={share.share_url} target="_blank" rel="noreferrer"
                  style={{ ...S.meta, fontSize: 11.5, color: GOLD }}>Open in Dropbox</a>
              )}
              <FilterBtn label={openId === share.id ? 'Hide files' : 'Files'} active={openId === share.id}
                onClick={() => setOpenId(openId === share.id ? null : share.id)} />
            </div>
            {openId === share.id && <ShareContents share={share} showToast={showToast} />}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The shares for this project: what is out there, what it costs, when it goes
 * away — and, below that, what has already gone. Bloat you can see is bloat
 * somebody can decide about; a sent folder that has been deleted is still
 * something you need to be able to answer questions about.
 */
export function SharesPanel({ projectCode, rev, onResend, showToast }) {
  const { shares, loading, refresh } = useAssetShares(projectCode, rev, { includePast: true });
  const [busyId, setBusyId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [showPast, setShowPast] = useState(false);

  const revoke = async (share) => {
    if (busyId) return;
    setBusyId(share.id);
    try {
      await revokeShare(share.id);
      showToast(`Deleted the share folder for "${share.label}"`);
      refresh();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const resend = async (share) => {
    try {
      onResend(await shareFiles(share.id), share);
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  if (loading && !shares.length) return <div style={{ ...S.meta, fontSize: 12 }}>Loading shares…</div>;

  const live = shares.filter(sh => sh.status === 'active');
  const past = shares.filter(sh => sh.status !== 'active');
  const totalBytes = live.reduce((sum, sh) => sum + Number(sh.total_bytes || 0), 0);
  const toggle = id => setOpenId(openId === id ? null : id);

  return (
    <div>
      {live.length > 0 ? (
        <div style={{ ...S.meta, fontSize: 11.5, marginBottom: 10 }}>
          {live.length} live share{live.length === 1 ? '' : 's'} holding {formatFileSize(totalBytes)} of
          copies in Dropbox. Each folder is deleted automatically on its date; deleting one
          early frees the space straight away. To send more images to someone who already has
          one of these links, select the files in the press kit, choose Share, and pick that
          share as the destination — the link stays the same.
        </div>
      ) : (
        <div style={{ ...S.meta, fontSize: 12, marginBottom: past.length ? 10 : 0 }}>
          No live shares. Select files in the press kit and choose Share to make one.
        </div>
      )}

      {live.map(share => (
        <div key={share.id} style={{ borderTop: '1px solid rgba(27,27,25,.12)', padding: '10px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ ...S.h3, fontSize: 15 }}>{share.label}</span>
            <span style={{ ...S.meta, fontSize: 11, color: daysUntil(share.expires_at) <= 3 ? '#7A6020' : '#6E6C66' }}>
              {expiryLabel(share.expires_at)}
            </span>
          </div>
          <div style={{ ...S.meta, fontSize: 11.5, margin: '3px 0 7px' }}>
            {share.description ? <span style={{ color: '#4A4842' }}>{share.description} · </span> : null}
            {share.file_count} file{share.file_count === 1 ? '' : 's'} ·
            {' '}{formatFileSize(share.total_bytes)} ·
            {' '}{resolutionLabel(share.requested_resolution)}
            {share.contact_label ? ` · sent to ${share.contact_label}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {share.share_url && (
              <a href={share.share_url} target="_blank" rel="noreferrer"
                style={{ ...S.meta, fontSize: 11.5, color: GOLD }}>Open in Dropbox</a>
            )}
            <FilterBtn label="Copy link" onClick={async () => {
              try {
                await navigator.clipboard.writeText(share.share_url || '');
                showToast('Link copied');
              } catch {
                showToast('Could not copy the link', 'error');
              }
            }} />
            <FilterBtn label={openId === share.id ? 'Hide files' : 'Files'} active={openId === share.id}
              onClick={() => toggle(share.id)} />
            <FilterBtn label="Send again at…" onClick={() => resend(share)} />
            <FilterBtn label={busyId === share.id ? 'Deleting…' : 'Delete now'} onClick={() => revoke(share)} />
          </div>
          {openId === share.id && <ShareContents share={share} showToast={showToast} />}
        </div>
      ))}

      {past.length > 0 && (
        <div style={{ borderTop: '1px solid rgba(27,27,25,.12)', marginTop: live.length ? 14 : 0, paddingTop: 10 }}>
          <FilterBtn
            label={showPast ? `Hide what has already gone (${past.length})` : `Already sent and deleted (${past.length})`}
            active={showPast} onClick={() => setShowPast(!showPast)} />
          {showPast && (
            <>
              <div style={{ ...S.meta, fontSize: 11.5, margin: '8px 0 4px' }}>
                These folders and their links are gone from Dropbox. What was in them is kept
                here, so the question of what was sent, and when, still has an answer.
              </div>
              {past.map(share => (
                <div key={share.id} style={{ borderTop: '1px solid rgba(27,27,25,.08)', padding: '8px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ ...S.h3, fontSize: 14, color: '#4A4842' }}>{share.label}</span>
                    <span style={{ ...S.meta, fontSize: 11 }}>
                      sent {new Date(share.created_at).toLocaleDateString()} ·
                      {' '}{share.status === 'revoked' ? 'deleted early' : 'expired'}
                      {share.purged_at ? ` ${new Date(share.purged_at).toLocaleDateString()}` : ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ ...S.meta, fontSize: 11.5 }}>
                      {share.description ? `${share.description} · ` : ''}
                      {share.file_count} file{share.file_count === 1 ? '' : 's'} ·
                      {' '}{formatFileSize(share.total_bytes)} ·
                      {' '}{resolutionLabel(share.requested_resolution)}
                      {share.contact_label ? ` · sent to ${share.contact_label}` : ''}
                    </span>
                    <FilterBtn label={openId === share.id ? 'Hide files' : 'Files'} active={openId === share.id}
                      onClick={() => toggle(share.id)} />
                    <FilterBtn label="Send these again" onClick={() => resend(share)} />
                  </div>
                  {openId === share.id && <ShareContents share={share} showToast={showToast} />}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
